import { useEffect, useMemo, useRef, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { Check, MessageTextSquare01, Monitor01, Phone01, Upload01 } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import { publicConsumerApi, readIntakeMeta, type PublicShopProfile } from "@/lib/api/public-consumer"
import type { ApiEstimate } from "@/lib/api/types"
import { publicOrigin } from "@/lib/intake/intake-config"
import { makeQr } from "@/lib/intake/qr"
import { FlowCta, FlowHeading, PhasePane, QuietLink } from "../flow-chrome"
import { captureFromEstimate } from "../capture/hydrate"
import { capturedCount, planFor } from "../capture/shot-plan"
import { shotTitle } from "../capture/capture-copy"
import { useEstimateWatch } from "./use-estimate-watch"

// Desktop → phone. The photos are taken with the phone: a QR code (and the
// same link by text) opens this request on it. Meanwhile this screen mirrors
// the phone live — connected, each photo as it arrives, done — and offers to
// finish the questions here. No phone handy? Upload from this computer.

type Stage = "waiting" | "connected" | "photos" | "done"

function maskPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "")
  return digits.length >= 4 ? `(•••) •••-${digits.slice(-4)}` : "your phone"
}

export function HandoffScreen({
  shop,
  token,
  customerPhone,
  textsAllowed,
  shopWantsVehicle,
  onUploadHere,
  onContinueHere,
}: {
  shop: PublicShopProfile
  token: string
  customerPhone: string
  // They agreed to texts on the contact screen. If not, nothing is sent
  // until they ask for the link themselves.
  textsAllowed: boolean
  shopWantsVehicle: boolean
  onUploadHere: () => void
  onContinueHere: (estimate: ApiEstimate) => void
}) {
  const reduce = useReducedMotion()
  const link = `${publicOrigin()}/go/${shop.id}?c=${token}&src=qr`
  const [qr, setQr] = useState<string | null>(null)
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [resending, setResending] = useState(false)
  const texted = useRef(false)

  useEffect(() => {
    let alive = true
    void makeQr(link, { size: 560 }).then((url) => {
      if (alive) setQr(url)
    })
    return () => {
      alive = false
    }
  }, [link])

  const { estimate, idle, resume } = useEstimateWatch(token, {
    stopWhen: (e) => !!readIntakeMeta(e)?.photosDoneAt,
  })
  const meta = estimate ? readIntakeMeta(estimate) : null

  // Text the link once (unless it already went out for this lead, or they
  // said no to texts).
  useEffect(() => {
    if (!meta || texted.current) return
    texted.current = true
    if (meta.handoff && meta.handoff.smsSentAt.length > 0) {
      setSentTo(maskPhone(customerPhone))
      return
    }
    if (!textsAllowed) return
    void publicConsumerApi
      .sendHandoff(token)
      .then((r) => setSentTo(r.sentTo))
      .catch(() => setSentTo(null))
  }, [meta, token, customerPhone, textsAllowed])

  const resend = async () => {
    setResending(true)
    try {
      const r = await publicConsumerApi.sendHandoff(token)
      setSentTo(r.sentTo)
    } catch {
      // keep the old line; the QR still works
    } finally {
      setResending(false)
    }
  }

  // What the phone has done so far, placed the same way the phone sees it.
  const mirror = useMemo(() => {
    if (!estimate) return null
    const capture = captureFromEstimate(estimate, { shopWantsVehicle })
    const plan = planFor(capture, estimate.vehicle?.vin ?? "", shopWantsVehicle)
    return { capture, plan, taken: capturedCount(plan, capture) + capture.extras.length }
  }, [estimate, shopWantsVehicle])

  const stage: Stage = meta?.photosDoneAt
    ? "done"
    : mirror && mirror.taken > 0
      ? "photos"
      : meta?.handoff?.continuedAt
        ? "connected"
        : "waiting"

  const statusLine: Record<Stage, string> = {
    waiting: "Waiting for your phone",
    connected: "Your phone is connected",
    photos: mirror ? `Taking photos · ${mirror.taken} so far` : "Taking photos",
    done: "Your photos are in",
  }

  return (
    <PhasePane className="pt-2">
      <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:gap-12">
        <section>
          <FlowHeading className="text-[30px] font-bold leading-[1.12] tracking-tight text-ink">
            Take the photos with your phone
          </FlowHeading>
          <p className="mt-3 max-w-md text-[16px] leading-relaxed text-zinc-600">
            Point your phone's camera at this code. It opens your request on the phone, ready for photos. It
            walks you around the car one photo at a time.
          </p>

          <figure className="mt-6 inline-flex flex-col items-center rounded-3xl bg-white p-5 ring-1 ring-ink/8">
            {qr ? (
              <img src={qr} alt="QR code that opens this request on your phone" className="size-[240px]" data-testid="handoff-qr" />
            ) : (
              <span className="grid size-[240px] place-items-center" aria-hidden>
                <span className="size-6 animate-spin rounded-full border-2 border-ink/15 border-t-ink" />
              </span>
            )}
            <figcaption className="mt-3 text-[14px] font-semibold text-ink">Point your phone's camera here</figcaption>
          </figure>

          <p className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[15px] text-zinc-600" aria-live="polite">
            <MessageTextSquare01 className="size-5 text-zinc-500" aria-hidden />
            {sentTo ? (
              <>We also texted the link to {sentTo}.</>
            ) : textsAllowed ? (
              <>We'll also text you the link.</>
            ) : (
              <>Or get the link by text.</>
            )}
            <button
              type="button"
              onClick={() => void resend()}
              disabled={resending}
              className="h-11 rounded-lg px-1 font-semibold text-ink underline decoration-ink/30 underline-offset-4 hover:decoration-ink disabled:opacity-50"
            >
              {resending ? "Sending…" : sentTo || textsAllowed ? "Text it again" : "Text me the link"}
            </button>
          </p>
        </section>

        <section aria-label="Your phone" className="rounded-3xl bg-white p-5 ring-1 ring-ink/8 lg:mt-2">
          <div className="flex items-center gap-3">
            <span
              className={cx(
                "grid size-10 place-items-center rounded-full",
                stage === "done" ? "bg-signal text-white" : stage === "waiting" ? "bg-ink/5 text-zinc-500" : "bg-signal/10 text-signal",
              )}
            >
              {stage === "done" ? <Check className="size-5" strokeWidth={3} aria-hidden /> : <Phone01 className="size-5" aria-hidden />}
            </span>
            <p className="text-[17px] font-semibold text-ink" role="status" data-testid="handoff-status">
              {statusLine[stage]}
            </p>
            {stage !== "done" && !idle && (
              <span className="ml-auto size-2.5 animate-pulse rounded-full bg-signal" aria-hidden />
            )}
          </div>

          {mirror && mirror.taken > 0 ? (
            <ul className="mt-5 grid grid-cols-3 gap-2.5" aria-label="Photos from your phone">
              <AnimatePresence initial={false}>
                {[
                  ...mirror.plan
                    .map((s) => ({ key: s.id, record: mirror.capture.shots[s.id], title: shotTitle(s) }))
                    .filter((x) => x.record),
                  ...mirror.capture.extras.map((r) => ({ key: r.key, record: r, title: "Extra photo" })),
                ].map((x) => (
                  <motion.li
                    key={x.key}
                    initial={reduce ? false : { opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 26 }}
                    className="min-w-0"
                  >
                    <img src={x.record!.previewUrl} alt={x.title} className="aspect-square w-full rounded-2xl object-cover" />
                    <p className="mt-1 truncate text-[13px] font-medium text-zinc-600">{x.title}</p>
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>
          ) : (
            <p className="mt-4 text-[15px] leading-relaxed text-zinc-600">
              {stage === "connected"
                ? "Photos will show up here as you take them."
                : "Once you open the link on your phone, you'll see it here."}
            </p>
          )}

          {idle && stage !== "done" && (
            <div className="mt-5 rounded-2xl bg-amber-50 p-4">
              <p className="text-[15px] font-medium text-amber-900">Still there? We stopped checking for your phone.</p>
              <button type="button" onClick={resume} className="mt-2 h-11 rounded-xl bg-white px-4 text-[15px] font-semibold text-ink ring-1 ring-ink/10">
                Keep watching
              </button>
            </div>
          )}

          {stage === "done" && estimate && (
            <div className="mt-6 space-y-1">
              <FlowCta onClick={() => onContinueHere(estimate)}>
                <Monitor01 className="size-5" aria-hidden />
                Answer the last questions here
              </FlowCta>
              <p className="text-center text-[14px] text-zinc-500">Or keep going on your phone.</p>
            </div>
          )}
        </section>
      </div>

      {stage !== "done" && (
        <div className="mt-10 border-t border-ink/8 pt-5">
          <QuietLink onClick={onUploadHere} className="mx-0">
            <Upload01 className="size-4" aria-hidden />
            No phone handy? Upload photos from this computer
          </QuietLink>
        </div>
      )}
    </PhasePane>
  )
}
